import { Chip, Tab, Tabs, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useSnackbar } from '@Providers/useSnackbar';
import useSubmissionsScope from './Hooks/useSubmissionsScope';
import SubmissionsFilters from './Components/SubmissionsFilters';
import GlobalTestSubmissions from './Components/GlobalTestSubmissions';
import GlobalAssignmentSubmissions from './Components/GlobalAssignmentSubmissions';
import { SubmissionsFilters as Filters, fetchPendingCounts } from './Services/submissions.req';

/**
 * The Breakdown subtab deep-links into the queue as query params. Read once
 * at mount, validated, one-way (the queue never writes the URL back).
 * `instructor_id` is stripped on the instructor portal — the backend 403s a
 * mismatched instructor there — and a product_id without a valid
 * product_type is meaningless, so it's dropped with it.
 */
const parseQueueParams = (search: string, isAdminPortal: boolean) => {
	const params = new URLSearchParams(search);
	const num = (key: string) => {
		const raw = params.get(key);
		const value = Number(raw);
		return raw !== null && raw !== '' && Number.isFinite(value) ? value : undefined;
	};
	const kindRaw = params.get('kind');
	const productTypeRaw = params.get('product_type');
	const productType =
		productTypeRaw === 'classroom' || productTypeRaw === 'standalone' ? productTypeRaw : undefined;
	return {
		kind:
			kindRaw === 'test' || kindRaw === 'assignment'
				? (kindRaw as 'test' | 'assignment')
				: undefined,
		filters: {
			instructor_id: isAdminPortal ? num('instructor_id') : undefined,
			product_type: productType,
			product_id: productType ? num('product_id') : undefined,
			from: params.get('from') || undefined,
			to: params.get('to') || undefined,
		} as Partial<Filters>,
	};
};

/**
 * The global Submissions tab: every essay attempt and assignment submission
 * across every product, filterable by Instructor → Product → Course → Unit.
 * Shared source compiled under both apps: the admin sees all instructors, the
 * instructor portal is pinned to the caller's own products server-side.
 *
 * Self-gated: the dashboard layout mounts every top-level route, so a
 * deep link by someone without the permission/grant renders the notice
 * below rather than the lists (the backend 403s the data calls anyway).
 */
const Submissions: React.FC = () => {
	const { isAdminPortal, canView, canGrade } = useSubmissionsScope();
	const { displaySnackbar } = useSnackbar();
	const location = useLocation();
	const [initial] = useState(() => parseQueueParams(location.search, isAdminPortal));
	const [kind, setKind] = useState<'test' | 'assignment'>(initial.kind ?? 'test');
	const [filters, setFilters] = useState<Filters>({ state: 'pending', ...initial.filters });
	const [counts, setCounts] = useState<{ tests: number; assignments: number }>();
	const [version, setVersion] = useState(0);

	// The unit list is per kind (test units vs assignment units): switching
	// tabs must drop the unit, or a test unit id silently empties the
	// assignments list and zeroes its count.
	const switchKind = (next: 'test' | 'assignment') => {
		if (next === kind) return;
		setKind(next);
		setFilters((current) => ({ ...current, unit_id: undefined }));
	};

	const countsFilters = useMemo(
		() => ({
			instructor_id: filters.instructor_id,
			product_type: filters.product_type,
			product_id: filters.product_id,
			course_id: filters.course_id,
			unit_id: filters.unit_id,
		}),
		[filters.instructor_id, filters.product_type, filters.product_id, filters.course_id, filters.unit_id]
	);

	const refreshCounts = useCallback(() => {
		fetchPendingCounts(countsFilters, kind)
			.then(setCounts)
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load pending counts');
			});
	}, [countsFilters, kind, displaySnackbar]);

	useEffect(() => {
		if (canView) refreshCounts();
	}, [canView, refreshCounts, version]);

	if (!canView) {
		return (
			<Wrapper>
				<Typography variant="h6">Submissions</Typography>
				<Notice>You do not have permission to view submissions.</Notice>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Header>
				<Typography variant="h5" fontWeight={700}>
					Submissions
				</Typography>
				<Chips>
					<Chip
						label={`Essays awaiting review: ${counts?.tests ?? '…'}`}
						sx={{ backgroundColor: '#ffab00', color: '#fff', fontWeight: 600 }}
					/>
					<Chip
						label={`Assignments pending: ${counts?.assignments ?? '…'}`}
						sx={{ backgroundColor: '#ffab00', color: '#fff', fontWeight: 600 }}
					/>
				</Chips>
			</Header>
			<Tabs value={kind} onChange={(_, value) => switchKind(value)} sx={{ marginBottom: '1.5rem' }}>
				<Tab value="test" label="Tests" />
				<Tab value="assignment" label="Assignments" />
			</Tabs>
			<SubmissionsFilters kind={kind} value={filters} onChange={setFilters} showInstructor={isAdminPortal} />
			{kind === 'test' ? (
				<GlobalTestSubmissions filters={filters} canGrade={canGrade} onChanged={() => setVersion((v) => v + 1)} />
			) : (
				<GlobalAssignmentSubmissions filters={filters} canGrade={canGrade} onChanged={() => setVersion((v) => v + 1)} />
			)}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 2rem;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
	margin-bottom: 1rem;
`;

const Chips = styled.div`
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const Notice = styled(Typography)`
	color: #a4a4a4;
	padding: 2rem 0;
`;

export default Submissions;
